"use client";

import { m as motion, useReducedMotion, type Variants } from "framer-motion";

type Props = {
  w1: string;
  w2: string;
  accent: string;
  w3: string;
};

const containerVariants: Variants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const innerVariants: Variants = {
  hidden: { y: "110%" },
  show: {
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  },
};

function Word({
  children,
  prefersReduced,
}: {
  children: React.ReactNode;
  prefersReduced: boolean;
}) {
  return (
    <span
      className="inline-block align-top overflow-hidden"
      style={{ verticalAlign: "top" }}
    >
      <motion.span
        className="inline-block"
        variants={prefersReduced ? undefined : innerVariants}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function HeroHeadline({ w1, w2, accent, w3 }: Props) {
  const prefersReduced = useReducedMotion() ?? false;

  return (
    <motion.h1
      className="mb-7 m-0 max-w-[1100px] font-display font-bold text-fg1"
      style={{
        fontSize: "clamp(36px, 6.5vw, 96px)",
        lineHeight: 1.2,
        letterSpacing: "-0.035em",
      }}
      variants={prefersReduced ? undefined : containerVariants}
      initial={false}
      animate={prefersReduced ? undefined : "show"}
    >
      <Word prefersReduced={prefersReduced}>{w1}</Word>{" "}
      <Word prefersReduced={prefersReduced}>{w2}</Word>{" "}
      <span
        className="inline-block align-top overflow-hidden"
        style={{ verticalAlign: "top" }}
      >
        <motion.span
          className="inline-block bg-clip-text"
          style={{
            background:
              "linear-gradient(92deg, #3B7EFF 0%, #22D3EE 55%, #3B7EFF 100%)",
            backgroundSize: "200% 100%",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
            lineHeight: 1.3,
          }}
          variants={prefersReduced ? undefined : innerVariants}
          animate={
            prefersReduced
              ? undefined
              : { backgroundPositionX: ["0%", "100%", "0%"] }
          }
          transition={
            prefersReduced
              ? undefined
              : {
                  duration: 6,
                  ease: "easeInOut",
                  repeat: Infinity,
                  delay: 1,
                }
          }
        >
          {accent}
        </motion.span>
      </span>{" "}
      <Word prefersReduced={prefersReduced}>{w3}</Word>
    </motion.h1>
  );
}
